import { useState } from "react";
import { Link } from "react-router-dom";
import { Search, Loader2, ArrowRight, AlertTriangle } from "lucide-react";
import { analyzeWebsite } from "@/lib/api";
import { AuditReportEmail } from "@/components/AuditReportEmail";
import LeadGenReadiness from "@/components/LeadGenReadiness";

const CATS = [
  ["seo", "SEO"], ["performance", "Performance"], ["mobile", "Mobile"], ["accessibility", "Accessibility"],
  ["content", "Content"], ["conversion", "Conversion"], ["security", "Security"], ["ai_readiness", "AI Readiness"],
];

const band = (s) => (s >= 85 ? ["Excellent", "text-emerald-600"] : s >= 70 ? ["Good", "text-emerald-700"] : s >= 50 ? ["Needs Improvement", "text-amber-600"] : s >= 30 ? ["Poor", "text-orange-600"] : ["Critical", "text-rose-600"]);

export default function KeralaAudit() {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");

  const run = async (e) => {
    e?.preventDefault();
    if (!url.trim()) return;
    setLoading(true); setErr(""); setRes(null);
    try {
      const r = await analyzeWebsite(url.trim());
      setRes(r);
    } catch { setErr("Couldn't analyze that URL. Please check it and try again."); }
    setLoading(false);
  };

  const [label, color] = res ? band(res.overall_score) : ["", ""];

  return (
    <div data-testid="kerala-audit">
      <section className="bg-gradient-to-br from-[#024E3F] to-[#012a22] text-white py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold">Free Website Health Check</h1>
          <p className="text-emerald-100/80 mt-4 md:text-lg">Enter your website and get your score across SEO, performance, mobile, security & AI-readiness — with actionable recommendations.</p>
          <form onSubmit={run} className="mt-8 flex flex-col sm:flex-row gap-3">
            <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="yourbusiness.in" data-testid="kerala-audit-input"
              className="flex-1 rounded-xl px-4 py-3.5 text-slate-900 outline-none" />
            <button type="submit" disabled={loading} data-testid="kerala-audit-submit"
              className="bg-white text-[#024E3F] font-semibold rounded-xl px-6 py-3.5 inline-flex items-center justify-center gap-2 disabled:opacity-60">
              {loading ? <Loader2 className="animate-spin" size={18} /> : <><Search size={17} /> Check now</>}
            </button>
          </form>
          {err && <p className="text-rose-300 text-sm mt-3" data-testid="kerala-audit-error">{err}</p>}
        </div>
      </section>

      {res && (
        <section className="py-14 bg-white" data-testid="kerala-audit-result">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {res.content_readable === false && (
              <div className="flex items-start gap-2.5 bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3.5 mb-8">
                <AlertTriangle size={17} className="text-amber-600 mt-0.5 shrink-0" />
                <p className="text-sm text-amber-800">We couldn't fully read <span className="font-semibold">{res.domain}</span>. Scores are conservative estimates.</p>
              </div>
            )}
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="text-center shrink-0">
                <div className="font-heading text-6xl font-extrabold text-slate-900">{res.overall_score}<span className="text-2xl text-slate-400">/100</span></div>
                <p className={`font-bold mt-1 ${color}`}>{label}</p>
                <p className="text-xs text-slate-400 mt-0.5">{res.domain}</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 flex-1 w-full">
                {CATS.map(([k, lbl]) => (
                  <div key={k} className="bg-emerald-50/60 border border-emerald-100 rounded-xl p-3 text-center">
                    <p className="font-heading text-xl font-bold text-slate-900">{res.categories?.[k] ?? "\u2014"}</p>
                    <p className="text-[11px] uppercase tracking-wide text-slate-400">{lbl}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-8 mb-8 rounded-2xl bg-emerald-50/60 border border-emerald-100 px-5 py-4 text-center">
              <p className="font-heading text-lg font-bold text-slate-900">We found {res.opportunity_count ?? 0} opportunities to improve {res.domain}.</p>
            </div>

            <div className="mb-8">
              <LeadGenReadiness url={res.final_url || res.url || url.trim()} source="kerala-audit" accent="#047857" />
            </div>

            <div className="max-w-2xl mx-auto">
              <AuditReportEmail auditId={res.id} brand="kerala" accentBg="bg-emerald-700" accentText="text-emerald-700" />
            </div>

            <div className="mt-10 text-center bg-slate-50 rounded-2xl p-8">
              <h3 className="font-heading text-xl font-bold text-slate-900">Want us to fix these for you?</h3>
              <p className="text-slate-500 mt-2">Get a tailored ₹ proposal from Kerala.com.</p>
              <Link to="/kerala/contact?service=Website%20Health%20Check" className="mt-5 inline-flex items-center gap-2 bg-emerald-700 text-white font-semibold rounded-xl px-6 py-3.5 hover:-translate-y-0.5 transition-transform">Get my proposal <ArrowRight size={17} /></Link>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
